"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export type CookieCategory = "necessary" | "statistics" | "marketing";

export interface CookieConsent {
  necessary: boolean; // always true
  statistics: boolean;
  marketing: boolean;
}

interface CookieConsentContextValue {
  consent: CookieConsent | null;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptSelected: (selected: CookieConsent) => void;
  openSettings: () => void;
}

const COOKIE_CONSENT_KEY = "jugarden_cookie_consent";
const COOKIE_CONSENT_VERSION = "1"; // bump to re-show banner after policy changes

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
  null,
);

function saveConsent(consent: CookieConsent) {
  const data = {
    consent,
    version: COOKIE_CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  document.cookie = `${COOKIE_CONSENT_KEY}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
}

function loadConsent(): CookieConsent | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_CONSENT_KEY}=([^;]*)`),
  );
  if (!match) return null;
  try {
    const data = JSON.parse(decodeURIComponent(match[1]));
    if (data.version !== COOKIE_CONSENT_VERSION) return null;
    return data.consent as CookieConsent;
  } catch {
    return null;
  }
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState({
    consent: null as CookieConsent | null,
    showBanner: false,
  });

  const { consent, showBanner } = state;

  useEffect(() => {
    const stored = loadConsent();
    setState({
      consent: stored,
      showBanner: !stored,
    });
  }, []);

  const setConsent = (c: CookieConsent) =>
    setState((s) => ({ ...s, consent: c }));
  const setShowBanner = (b: boolean) =>
    setState((s) => ({ ...s, showBanner: b }));

  const applyConsent = useCallback((newConsent: CookieConsent) => {
    setConsent(newConsent);
    saveConsent(newConsent);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    applyConsent({ necessary: true, statistics: true, marketing: true });
  }, [applyConsent]);

  const rejectAll = useCallback(() => {
    applyConsent({ necessary: true, statistics: false, marketing: false });
  }, [applyConsent]);

  const acceptSelected = useCallback(
    (selected: CookieConsent) => {
      applyConsent({ ...selected, necessary: true });
    },
    [applyConsent],
  );

  const openSettings = useCallback(() => {
    setShowBanner(true);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        acceptAll,
        rejectAll,
        acceptSelected,
        openSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    // Safe default for SSR / prerendering when provider is not yet mounted
    return {
      consent: null,
      showBanner: false,
      acceptAll: () => {},
      rejectAll: () => {},
      acceptSelected: () => {},
      openSettings: () => {},
    } satisfies CookieConsentContextValue;
  }
  return context;
}

// Type augmentation for window.gtag
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}
